import { Link } from "react-router-dom";
import { Pill } from "./PortalPage";
import { ProgressStepper } from "./ProgressStepper";
import { fmtDate, fmtDateTime, fmtMoney, requestStatusMap, JOB_STATUS, EMERGENCY_STATUS, PROJECT_STATUS, QUOTE_STATUS, INVOICE_STATUS, URGENCY, titleCase } from "./portalUtils";
import { CONTRACT_STATUS, fmtDay } from "../contracts/contractUtils";
import { Buildings, Users, EnvelopeSimple, ArrowRight } from "@phosphor-icons/react";
import { TIER_LABEL } from "./portalUtils";
import { useSubClientLinks } from "./SubClientCards";

/* Compact row renderers for the sub-client oversight tabs — every row links to the normal portal detail page. */

const Row = ({ to, testid, children }) => (
  <Link to={to} className="flex items-center gap-3 px-4 py-2.5 hover:bg-muted/40 transition-colors" data-testid={testid}>{children}</Link>
);
const Ref = ({ children }) => <span className="text-[10px] font-mono text-muted-foreground">{children}</span>;

export function RequestRow({ r }) {
  const links = useSubClientLinks();
  return (
    <Row to={links.request(r.id)} testid={`sub-request-${r.id}`}>
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-2 flex-wrap"><Ref>{r.reference}</Ref><Pill map={requestStatusMap(r)} value={r.status} />{r.priority === "urgent" && <span className="text-[9px] px-1.5 py-0.5 rounded bg-red-100 text-red-700 font-semibold">URGENT</span>}</div>
        <p className="text-sm font-medium truncate">{r.title}</p>
        <p className="text-[11px] text-muted-foreground truncate">{r.address || "—"} · raised {fmtDate(r.created_at)} · {titleCase(r.category)}</p>
      </div>
      <div className="hidden md:block w-[40%] shrink-0"><ProgressStepper progress={r.progress} compact /></div>
    </Row>
  );
}

export function JobRow({ j }) {
  const links = useSubClientLinks();
  return (
    <Row to={links.job(j.id)} testid={`sub-job-${j.id}`}>
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-2 flex-wrap"><Ref>{j.reference}</Ref><Pill map={JOB_STATUS} value={j.status} /></div>
        <p className="text-sm font-medium truncate">{j.title}</p>
        <p className="text-[11px] text-muted-foreground truncate">{j.address || "—"}{j.contractor ? ` · ${j.contractor}` : ""}</p>
      </div>
      <div className="text-right shrink-0 text-[11px] text-muted-foreground">{j.scheduled_start ? <>Scheduled<br /><span className="font-medium text-foreground">{fmtDateTime(j.scheduled_start)}</span></> : j.completed_at ? <>Completed<br /><span className="font-medium text-foreground">{fmtDate(j.completed_at)}</span></> : fmtDate(j.created_at)}</div>
    </Row>
  );
}

export function EmergencyRow({ e }) {
  const links = useSubClientLinks();
  return (
    <Row to={links.emergency(e.id)} testid={`sub-emergency-${e.id}`}>
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-2 flex-wrap"><Ref>{e.reference}</Ref><Pill map={EMERGENCY_STATUS} value={e.status} />{e.urgency && <Pill map={URGENCY} value={e.urgency} />}</div>
        <p className="text-sm font-medium truncate">{e.title}</p>
        <p className="text-[11px] text-muted-foreground truncate">{e.address || "—"}{e.contractor ? ` · ${e.contractor}` : ""} · reported {fmtDateTime(e.created_at)}</p>
      </div>
      <ArrowRight size={14} className="text-muted-foreground shrink-0" />
    </Row>
  );
}

export function ProjectRow({ p }) {
  const links = useSubClientLinks();
  return (
    <Row to={links.project(p.id)} testid={`sub-project-${p.id}`}>
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-2 flex-wrap"><Ref>{p.reference}</Ref><Pill map={PROJECT_STATUS} value={p.status} /></div>
        <p className="text-sm font-medium truncate">{p.name}</p>
        <p className="text-[11px] text-muted-foreground truncate">{p.address || "—"}{p.start_date ? ` · starts ${fmtDate(p.start_date)}` : ""}{p.target_date ? ` · due ${fmtDate(p.target_date)}` : ""}</p>
      </div>
      <div className="w-28 shrink-0">
        <div className="flex justify-between text-[10px] text-muted-foreground"><span>{p.progress?.completed ?? 0}/{p.progress?.total ?? 0} tasks</span><span>{p.progress?.pct ?? 0}%</span></div>
        <div className="h-1.5 rounded-full bg-muted mt-1"><div className="h-1.5 rounded-full bg-violet-500" style={{ width: `${p.progress?.pct ?? 0}%` }} /></div>
      </div>
    </Row>
  );
}

export function ContractRow({ c }) {
  const links = useSubClientLinks();
  return (
    <Row to={links.contract(c.id)} testid={`sub-contract-${c.id}`}>
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-2 flex-wrap"><Ref>{c.reference}</Ref><Pill map={CONTRACT_STATUS} value={c.status} /></div>
        <p className="text-sm font-medium truncate">{c.title}</p>
        <p className="text-[11px] text-muted-foreground truncate">{c.address || "—"} · {c.schedule_text}</p>
      </div>
      <div className="text-right shrink-0 text-[11px] text-muted-foreground">Next visit<br /><span className="font-medium text-foreground">{c.next_visit ? fmtDay(c.next_visit, { weekday: "short", day: "numeric", month: "short" }) : "—"}</span><br />{c.visits_upcoming} upcoming · {c.visits_completed} done</div>
    </Row>
  );
}

export function QuoteRow({ q }) {
  const links = useSubClientLinks();
  const isRequestQuote = !!q.jr_id;
  return (
    <Row to={isRequestQuote ? links.request(q.jr_id) : links.quote(q.id)} testid={`sub-quote-${q.jr_id || q.id}`}>
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-2 flex-wrap"><Ref>{q.reference}</Ref><Pill map={isRequestQuote ? requestStatusMap(q) : QUOTE_STATUS} value={q.status} /></div>
        <p className="text-sm font-medium truncate">{q.title}</p>
        <p className="text-[11px] text-muted-foreground truncate">{q.address || "—"} · {fmtDate(q.sent_at || q.updated_at || q.created_at)}</p>
      </div>
      <span className="text-sm font-semibold font-[Chivo] shrink-0">{fmtMoney(q.total_inc_vat ?? q.grand_total ?? q.total ?? q.amount)}</span>
    </Row>
  );
}

export function InvoiceRow({ inv }) {
  const links = useSubClientLinks();
  const overdue = inv.status !== "paid" && inv.status !== "cancelled" && inv.due_date && inv.due_date < new Date().toISOString().slice(0, 10);
  return (
    <Row to={links.invoice(inv.id)} testid={`sub-invoice-${inv.id}`}>
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-2 flex-wrap"><Ref>{inv.reference || inv.invoice_number}</Ref><Pill map={INVOICE_STATUS} value={overdue ? "overdue" : inv.status} /></div>
        <p className="text-sm font-medium truncate">{inv.title || inv.job_title || inv.job_reference || "Invoice"}</p>
        <p className="text-[11px] text-muted-foreground truncate">{fmtDate(inv.issue_date || inv.created_at)}{inv.due_date ? ` · due ${fmtDate(inv.due_date)}` : ""}{inv.client_po_number ? ` · PO ${inv.client_po_number}` : ""}</p>
      </div>
      <span className="text-sm font-semibold font-[Chivo] shrink-0">{fmtMoney(inv.amount)}</span>
    </Row>
  );
}

export function PropertyRow({ p }) {
  return (
    <li className="flex items-center gap-3 px-4 py-2.5" data-testid={`sub-property-${p.id}`}>
      <Buildings size={16} className="text-muted-foreground shrink-0" />
      <div className="min-w-0"><p className="text-sm font-medium truncate">{p.name}</p><p className="text-[11px] text-muted-foreground truncate">{[p.address_line, p.postcode].filter(Boolean).join(", ")}{p.property_type ? ` · ${p.property_type}` : ""}</p></div>
    </li>
  );
}

export function TeamRow({ u }) {
  return (
    <li className="flex items-center gap-3 px-4 py-2.5" data-testid={`sub-team-${u.email}`}>
      <div className="h-8 w-8 rounded-full bg-muted flex items-center justify-center shrink-0"><Users size={14} className="text-muted-foreground" /></div>
      <div className="flex-1 min-w-0"><p className="text-sm font-medium truncate">{u.name || u.email}</p><p className="text-[11px] text-muted-foreground truncate inline-flex items-center gap-1"><EnvelopeSimple size={11} /> {u.email}</p></div>
      <div className="text-right shrink-0"><p className="text-[11px] font-medium">{TIER_LABEL[u.tier] || "User"}</p><p className="text-[10px] text-muted-foreground">{u.active ? (u.last_login ? `Last in ${fmtDate(u.last_login)}` : "Never signed in") : "Deactivated"}</p></div>
    </li>
  );
}
